import axios from 'axios';
import { useCallback, useEffect, useRef, useState } from 'react';

export const BACKEND = process.env.REACT_APP_BACKEND_URL;
const K = `${BACKEND}/api/karaoke`;

export const THEMES = [
  { key: 'purple_stage', label: 'Purple Stage' },
  { key: 'blue_concert', label: 'Blue Concert' },
  { key: 'golden_spotlight', label: 'Golden Spotlight' },
];

export const errMsg = (e, fallback = 'Something went wrong') => {
  const d = e?.response?.data?.detail;
  return typeof d === 'string' ? d : d?.message || fallback;
};

export const kApi = {
  me: () => axios.get(`${K}/me`).then((r) => r.data),
  state: (since) => axios.get(`${K}/state`, { params: { since } }).then((r) => r.data),
  display: () => axios.get(`${K}/display`).then((r) => r.data),
  settings: () => axios.get(`${K}/settings`).then((r) => r.data),
  saveSettings: (patch) => axios.put(`${K}/settings`, patch).then((r) => r.data),
  uploadMusic: (theme, file, onProgress) => {
    const fd = new FormData();
    fd.append('file', file);
    return axios.post(`${K}/music/${theme}`, fd, { onUploadProgress: (p) => onProgress?.(Math.round((p.loaded / (p.total || 1)) * 100)) }).then((r) => r.data);
  },
  removeMusic: (theme) => axios.delete(`${K}/music/${theme}`).then((r) => r.data),
  startSession: () => axios.post(`${K}/sessions`).then((r) => r.data),
  endSession: (id) => axios.post(`${K}/sessions/${id}/end`).then((r) => r.data),
  sessions: () => axios.get(`${K}/sessions`).then((r) => r.data),
  staffRequest: (body) => axios.post(`${K}/requests`, body).then((r) => r.data),
  add: (requestId, idx) => axios.post(`${K}/playlist`, { request_id: requestId, song_idx: idx }).then((r) => r.data),
  reorder: (ids) => axios.put(`${K}/playlist/order`, { ids }),
  remove: (id) => axios.delete(`${K}/playlist/${id}`),
  start: (id) => axios.post(`${K}/playlist/${id}/start`),
  stop: () => axios.post(`${K}/playback/stop`),
  ended: (id, nonce) => axios.post(`${K}/playback/${id}/ended`, { play_nonce: nonce }),
  error: (id, code) => axios.post(`${K}/playback/${id}/error`, { code }),
  mark: (id, status) => axios.post(`${K}/playlist/${id}/mark`, { status }),
  setMode: (mode) => axios.put(`${K}/display`, { mode }),
  team: () => axios.get(`${K}/team`).then((r) => r.data),
  setTeam: (userId, role) => axios.put(`${K}/team`, { user_id: userId, role }),
  ownerHotels: () => axios.get(`${K}/owner/hotels`).then((r) => r.data),
  ownerSet: (hotelId, patch) => axios.put(`${K}/owner/hotels/${hotelId}`, patch),
  qrBlob: () => axios.get(`${K}/qr.png`, { params: { base: window.location.origin }, responseType: 'blob' }).then((r) => r.data),
};

export const pub = {
  info: (code) => axios.get(`${K}/public/${code}`).then((r) => r.data),
  submit: (code, body) => axios.post(`${K}/public/${code}/requests`, body).then((r) => r.data),
  mine: (code, tokens) => axios.get(`${K}/public/${code}/mine`, { params: { tokens: tokens.join(',') } }).then((r) => r.data),
  search: (code, q, more = false) => axios.get(`${K}/public/${code}/search`, { params: { q, more } }).then((r) => r.data),
};

export const parseYouTubeId = (s) => {
  const v = (s || '').trim();
  if (/^[A-Za-z0-9_-]{11}$/.test(v)) return v;
  if (!/^https?:\/\/(www\.|m\.|music\.)?(youtube\.com|youtu\.be)\//.test(v)) return null;
  const m = v.match(/(?:youtube\.com\/(?:watch\?(?:.*&)?v=|embed\/|shorts\/|live\/)|youtu\.be\/)([A-Za-z0-9_-]{11})/);
  return m ? m[1] : null;
};

// Live state for staff screens: polls, but only downloads the full state when the version changed.
export function useKaraokeState(intervalMs = 2000) {
  const [data, setData] = useState(null);
  const ver = useRef(0);
  const refresh = useCallback(async (force = false) => {
    try {
      const d = await kApi.state(force ? 0 : ver.current);
      if (d.unchanged) return;
      ver.current = d.version || 0;
      setData(d);
    } catch { /* keep last state */ }
  }, []);
  useEffect(() => {
    refresh(true);
    const t = setInterval(() => refresh(), intervalMs);
    return () => clearInterval(t);
  }, [refresh, intervalMs]);
  return [data, () => refresh(true)];
}

export const STATUS_STYLE = {
  waiting: ['Waiting', 'bg-slate-100 text-slate-700'],
  in_playlist: ['In Playlist', 'bg-sky-100 text-sky-800'],
  up_next: ['Up Next', 'bg-amber-100 text-amber-900'],
  playing: ['Now Singing', 'bg-fuchsia-600 text-white'],
  played: ['Played', 'bg-emerald-100 text-emerald-800'],
  skipped: ['Skipped', 'bg-rose-100 text-rose-800'],
  ready: ['Ready', 'bg-sky-100 text-sky-800'],
};
